import React, { useState, useEffect } from "react";

import { Timeline, Row, Col, Card, Table, Tag, Typography } from "antd";

import { CheckCircleOutlined, SyncOutlined } from "@ant-design/icons";

import axios from "axios";

import { useParams, useOutletContext } from "react-router-dom";

const { Text } = Typography;

const MessageStatus = () => {
  // =====================================================
  // STATES
  // =====================================================

  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  // =====================================================
  // PARAMS
  // =====================================================

  const { contactId } = useParams();

  // =====================================================
  // GET PARENT FUNCTION
  //
  // Status.jsx se ye function milega.
  // =====================================================

  const { updateContactDetails } = useOutletContext();

  // =====================================================
  // STATUS PRIORITY
  // =====================================================

  const priority = {
    read: 3,
    delivered: 2,
    sent: 1,
    failed: 0,
  };

  // =====================================================
  // TEXT CELL RENDER
  // =====================================================

  const getTextCellRender = () => {
    return {
      render: (text) => <Text>{text}</Text>,
    };
  };

  // =====================================================
  // FETCH STATUS
  // =====================================================

  useEffect(() => {
    const fetchContactDetails = async () => {
      try {
        setLoading(true);

        setError("");

        // =================================================
        // GET WABA STATUS
        // =================================================

        const response = await axios.get(`/api/contact/getstatus/${contactId}`);

        console.log("Contact status response:", response.data);

        // =================================================
        // VERY IMPORTANT
        //
        // Backend returns:
        //
        // contact: updatedContact
        //
        // updatedContact contains latest leadscore.
        //
        // We send it to parent Status.jsx.
        // =================================================

        if (response.data?.contact) {
          console.log("Latest contact from getstatus:", response.data.contact);

          updateContactDetails(response.data.contact);
        }

        // =================================================
        // GET MESSAGES
        // =================================================

        const messages = Array.isArray(response.data?.messages)
          ? response.data.messages
          : [];

        console.log("Messages received:", messages.length);

        // =================================================
        // NO MESSAGES
        // =================================================

        if (messages.length === 0) {
          setData([]);

          setError(
            "This Contact's Status is not Found in WABA Reports.\n\nReason: The contact has not received any messages yet or this contact is not being used in any campaign from the last 7 days.",
          );

          return;
        }

        // =================================================
        // CONVERT MESSAGES
        // =================================================

        const entries = messages
          .map((item) => {
            const entry = {};

            // =========================================
            // CREATED AT
            // =========================================

            const datetime = item?.createdAt
              ? new Date(item.createdAt)
              : new Date();

            entry.timestamp = datetime.valueOf();

            entry.time = datetime.toLocaleString("sv");

            // =========================================
            // FROM
            // =========================================

            entry.from = item?.metadata?.display_phone_number || "";

            // =========================================
            // TO
            // =========================================

            entry.to = item?.statuses?.[0]?.recipient_id || "";

            // =========================================
            // TYPE
            // =========================================

            entry.type = item?.statuses?.[0]?.conversation?.origin?.type || "";

            // =========================================
            // DEFAULT STATUS
            // =========================================

            entry.status = "failed";

            // =========================================
            // TIMELINE
            // =========================================

            entry.timeline = {};

            // =========================================
            // PROCESS STATUSES
            // =========================================

            if (Array.isArray(item?.statuses)) {
              item.statuses.forEach((status) => {
                if (!status) {
                  return;
                }

                const currentStatus = String(status.status || "").toLowerCase();

                // ===================================
                // STATUS PRIORITY
                // ===================================

                if (
                  priority?.[entry.status] !== undefined &&
                  priority?.[currentStatus] !== undefined &&
                  priority[entry.status] < priority[currentStatus]
                ) {
                  entry.status = currentStatus;
                }

                // ===================================
                // TIMELINE
                // ===================================

                if (currentStatus) {
                  entry.timeline[currentStatus] = status.timestamp;
                }
              });
            }

            // =========================================
            // WHATSAPP ID
            // =========================================

            entry.waId = item?.statuses?.[0]?.id || "";

            // =========================================
            // SYSTEM ID
            // =========================================

            entry.sysId = item?._id || "";

            // =========================================
            // TABLE KEY
            // =========================================

            entry.key = item?._id || item?.statuses?.[0]?.id || "";

            // =========================================
            // ERRORS
            // =========================================

            entry.errors = item?.statuses?.[0]?.errors || [];

            return entry;
          })
          .filter(Boolean);

        // =================================================
        // SORT LATEST FIRST
        // =================================================

        entries.sort((a, b) => Number(b.timestamp) - Number(a.timestamp));

        // =================================================
        // UPDATE MESSAGE TABLE
        // =================================================

        setData(entries);
      } catch (error) {
        console.error("Error fetching contact details:", error);

        setData([]);

        setError(
          error?.response?.data?.message || "Error fetching contact details",
        );
      } finally {
        setLoading(false);
      }
    };

    if (contactId) {
      fetchContactDetails();
    }
  }, [contactId, updateContactDetails]);

  // =====================================================
  // PARSE WHATSAPP TIMESTAMP
  // =====================================================

  const parseDateTime = (timestamp) => {
    if (timestamp) {
      return new Date(Number(timestamp) * 1000).toLocaleString("sv");
    }

    return "————";
  };

  // =====================================================
  // TABLE COLUMNS
  // =====================================================

  const columns = [
    {
      title: "To",
      dataIndex: "to",
      key: "to",
    },

    {
      title: "Creation Time",
      dataIndex: "time",
      key: "time",

      sorter: (a, b) => {
        return Number(a.timestamp) - Number(b.timestamp);
      },
    },

    {
      title: "Last Status",
      dataIndex: "status",
      key: "status",

      render: (status) => {
        const normalizedStatus = String(status || "").toLowerCase();

        let color;

        const displayStatus = normalizedStatus
          ? normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1)
          : "Unknown";

        switch (displayStatus) {
          case "Delivered":
            color = "orange";
            break;

          case "Read":
            color = "green";
            break;

          case "Sent":
            color = "blue";
            break;

          case "Failed":
            color = "red";
            break;

          default:
            color = "gray";
        }

        return <Tag color={color}>{displayStatus}</Tag>;
      },
    },

    {
      title: "Whatsapp ID",
      dataIndex: "waId",
      key: "waId",

      ...getTextCellRender(),
    },
  ];

  // =====================================================
  // EXPANDED ROW
  // =====================================================

  const expandedRowRender = (record) => {
    const renderTimeline = () => {
      const timelineItems = [];

      // =================================================
      // READ
      // =================================================

      if (record.status?.toLowerCase() === "read") {
        timelineItems.push(
          {
            children: "Accepted",

            dot: <SyncOutlined />,

            color: "black",

            label: (
              <span
                style={{
                  float: "right",
                }}
              >
                {parseDateTime(record.timeline.sent)}
              </span>
            ),
          },

          {
            children: "Sent",

            dot: <CheckCircleOutlined />,

            label: (
              <span
                style={{
                  float: "right",
                }}
              >
                {parseDateTime(record.timeline.sent)}
              </span>
            ),
          },

          {
            children: "Delivered",

            dot: <CheckCircleOutlined />,

            color: "orange",

            label: (
              <span
                style={{
                  float: "right",
                }}
              >
                {parseDateTime(record.timeline.delivered)}
              </span>
            ),
          },

          {
            children: "Read",

            dot: <CheckCircleOutlined />,

            color: "green",

            label: (
              <span
                style={{
                  float: "right",
                }}
              >
                {parseDateTime(record.timeline.read)}
              </span>
            ),
          },
        );
      }

      // =================================================
      // DELIVERED
      // =================================================
      else if (record.status?.toLowerCase() === "delivered") {
        timelineItems.push(
          {
            children: "Accepted",

            dot: <SyncOutlined />,

            color: "black",

            label: (
              <span
                style={{
                  float: "right",
                }}
              >
                {parseDateTime(record.timeline.sent)}
              </span>
            ),
          },

          {
            children: "Sent",

            dot: <CheckCircleOutlined />,

            label: (
              <span
                style={{
                  float: "right",
                }}
              >
                {parseDateTime(record.timeline.sent)}
              </span>
            ),
          },

          {
            children: "Delivered",

            dot: <CheckCircleOutlined />,

            color: "orange",

            label: (
              <span
                style={{
                  float: "right",
                }}
              >
                {parseDateTime(record.timeline.delivered)}
              </span>
            ),
          },
        );
      }

      // =================================================
      // SENT
      // =================================================
      else if (record.status?.toLowerCase() === "sent") {
        timelineItems.push(
          {
            children: "Accepted",

            dot: <SyncOutlined />,

            color: "black",

            label: (
              <span
                style={{
                  float: "right",
                }}
              >
                {parseDateTime(record.timeline.sent)}
              </span>
            ),
          },

          {
            children: "Sent",

            dot: <CheckCircleOutlined />,

            label: (
              <span
                style={{
                  float: "right",
                }}
              >
                {parseDateTime(record.timeline.sent)}
              </span>
            ),
          },
        );
      }

      // =================================================
      // FAILED
      // =================================================
      else if (record.status?.toLowerCase() === "failed") {
        if (Array.isArray(record.errors) && record.errors.length > 0) {
          return record.errors.map((error, index) => (
            <div
              key={index}
              style={{
                border: "1px solid #ccc",

                padding: "10px",

                marginBottom: "10px",
              }}
            >
              <p>
                <strong>Error Code:</strong> {error.code || "N/A"}
              </p>

              <p>
                <strong>Error Message:</strong> {error.title || "N/A"}
              </p>

              <p>
                <strong>Error Data:</strong>{" "}
                {error.error_data?.details || "N/A"}
              </p>
            </div>
          ));
        }

        return <div>No error information available.</div>;
      }

      // =================================================
      // TIMELINE
      // =================================================

      return <Timeline mode="left" items={timelineItems} />;
    };

    return (
      <Row>
        <Col span={10} offset={2}>
          <Card hoverable>{renderTimeline()}</Card>
        </Col>

        <Col span={8} offset={2}>
          <Card title="Preview Message" hoverable>
            {/* Render preview message here */}
          </Card>
        </Col>
      </Row>
    );
  };

  // =====================================================
  // RETURN
  // =====================================================

  return (
    <div className="page-content w-100">
      {error && (
        <div
          className="alert alert-danger"
          style={{
            whiteSpace: "pre-line",
          }}
        >
          {error}
        </div>
      )}

      <div className="row">
        <div className="col-xxl-12">
          <div id="contactList" className="card">
            <div className="card-body">
              <Table
                dataSource={data}
                columns={columns}
                loading={loading}
                scroll={{
                  y: data.length > 10 ? 500 : null,
                }}
                expandable={{
                  expandedRowRender,
                }}
                components={{
                  header: {
                    cell: ({ style, ...restProps }) => (
                      <th
                        {...restProps}
                        style={{
                          ...style,
                          backgroundColor: "#c6c6c6",
                          color: "black",
                          textAlign: "center",
                        }}
                      />
                    ),
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageStatus;
